import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { IDataDetalharCooperado } from "../../../../../../../types/api/cadastros/detalhar-cooperado";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";

export default function DialogEditarDadosFinanceiros({
    open,
    onClose,
    data
}: DialogEditarDadosFinanceirosProps) {

    const isPessoaJuridica = useMemo(() => data.documento_pessoa !== undefined && data.documento_pessoa !== "" && data.documento_pessoa.length === 14, [data])

    const queryClient = useQueryClient();

    const methods = useForm<FormData>({
        defaultValues: {
            aliquota_senar: `${data.aliquota_senar}` || "0",
            aliquota_inss: `${data.aliquota_inss}` || "0"
        }
    })
    const { control, handleSubmit } = methods;

    //queries e mutations
    const { mutate: editar, isLoading } = useMutation({
        mutationKey: 'editar-cooperado',
        mutationFn: (inputs: EditarCooperado) => axiosClient.put(`/cadastros/editar-cooperado/${data.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao editar cooperado!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Cooperado editado com sucesso`, { variant: "success" });
            queryClient.invalidateQueries('cadastros/detalhar-cooperado')
            onClose()
        }
    })

    //

    //funções manipuladoras

    const onSubmit = (data: FormData) => {
        const params: EditarCooperado = {
            campos: "Dados Financeiros",
            banco: data.banco?.codigo || "",
            agencia: data.agencia,
            conta: data.conta,
            digito_conta: data.digito_conta || null,
            digito_agencia: data.digito_agencia || null,
            aliquota_senar: data.aliquota_senar,
            aliquota_inss: data.aliquota_inss
        }

        editar(params);
    }

    return (
        <FormProvider {...methods}>
            <Dialog
                maxWidth="xs"
                fullWidth
                open={open}
                onClose={onClose}
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Editar Dados Financeiros</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={"aliquota_inss"}
                                control={control}
                                rules={{ validate: (value) => (!value || parseFloat(value) <= 100) || "A alíquota deve ser menor ou igual a 100%" }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        defaultValue={isPessoaJuridica ? 2.75 : 2.30}
                                        label='Aliquota INSS'
                                        type="number"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange((e.target.value))}
                                        slotProps={{ htmlInput: { max: 100 } }}
                                        error={invalid}
                                        helperText={error ? error.message : ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={"aliquota_senar"}
                                control={control}
                                rules={{ validate: (value) => parseFloat(value) <= 100 || "A alíquota deve ser menor ou igual a 100%" }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        label='Aliquota SENAR'
                                        defaultValue={0.20}
                                        type="number"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange((e.target.value))}
                                        slotProps={{ htmlInput: { max: 100 } }}
                                        error={invalid}
                                        helperText={error ? error.message : ""}
                                    />
                                )}
                            />
                        </Grid>

                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={onClose}>Cancelar</Button>
                    <Button disabled={isLoading} onClick={handleSubmit(onSubmit)} color="primary" variant="contained">Editar</Button>
                </DialogActions>
            </Dialog>

        </FormProvider>
    )
}

interface DialogEditarDadosFinanceirosProps {
    open: boolean;
    onClose: () => void;
    data: IDataDetalharCooperado
}

type FormData = {
    banco: {
        codigo: string;
        nome: string;
    } | null;
    agencia: string;
    conta: string;
    digito_conta: string | null;
    digito_agencia: string | null;
    aliquota_senar: string;
    aliquota_inss: string;
}

type EditarCooperado = {
    campos: "Dados Financeiros";
    banco: string;
    agencia: string;
    conta: string;
    digito_conta: string | null;
    digito_agencia: string | null;
    aliquota_senar: string;
    aliquota_inss: string;
}